import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { API } from 'api';
import { PaymentMethodData } from 'shared/types';
import { useAuthState } from 'state/useAuthState';

type UseSavedPaymentMethodProps = {
  userId?: number;
  verificationToken?: string;
  useLoggedInState?: boolean;
  paymentType?: 'credit_card' | 'apple_pay' | 'paypal_billing_agreement';
};

type UseSavedPaymentMehodResult = Omit<
  UseQueryResult<PaymentMethodData | undefined, Error>,
  'data'
> & {
  savedPaymentMethod?: PaymentMethodData;
};

const getPaymentMethod = async (
  userId: number,
  verificationToken: string,
): Promise<PaymentMethodData> => {
  const params = {
    ...(userId && { userId }),
    ...(verificationToken && { verificationToken }),
  };

  const res = await API.get(`recurly_integration/payment_method/`, {
    params,
  });
  return res.data;
};

export const useSavedPaymentMethod = (
  props: UseSavedPaymentMethodProps,
): UseSavedPaymentMehodResult => {
  const { userId, verificationToken, useLoggedInState, paymentType } = props;
  const { user } = useAuthState();

  const { data: savedPaymentMethod, ...result } = useQuery({
    queryKey: ['saved-payment-method', userId],
    queryFn: () =>
      getPaymentMethod(userId as number, verificationToken as string),
    enabled:
      (Boolean(userId) && Boolean(verificationToken)) ||
      (Boolean(useLoggedInState) && Boolean(user)),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: 'always',
    select: (data) => {
      if (!data || !data.paymentType) {
        return undefined;
      }

      if (paymentType && data.paymentType !== paymentType) {
        return undefined;
      }
      return data;
    },
  });

  return { ...result, savedPaymentMethod };
};
